
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useReports } from "@/hooks/useReports";
import { NotificationIcon } from "@/components/notifications/NotificationIcon";
import { NotificationType } from "@/components/notifications/types";
import { Bell, CheckCheck, Download, MailOpen } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface Notification {
  id: string;
  type: string;
  message?: string;
  campaign_id?: string | null;
  created_at?: string | null;
}

interface NotificationDropdownProps {
  notifications: Notification[] | undefined;
  unreadCount?: number;
  onMarkAsRead: (id: string) => void;
  onMarkAllAsRead?: () => void;
  hasUnread?: boolean;
}

export function NotificationDropdown({
  notifications,
  unreadCount = 0,
  onMarkAsRead,
  onMarkAllAsRead,
  hasUnread,
}: NotificationDropdownProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { reports } = useReports();

  const formatNotificationDate = (date: string | null | undefined) => {
    if (!date) return "Date inconnue";
    try {
      return format(new Date(date), "dd/MM/yyyy 'à' HH:mm", { locale: fr });
    } catch {
      return "Date inconnue";
    }
  };

  const handleDownloadReport = (notification: Notification) => {
    if (!notification.campaign_id) {
      toast({
        title: "Erreur",
        description: "Impossible d'ouvrir le rapport : données manquantes",
        variant: "destructive",
      });
      return;
    }

    // Trouver le rapport correspondant à cette campagne
    const report = reports?.find(r => r.campaign_id === notification.campaign_id);
    
    if (!report) {
      toast({
        title: "Rapport non trouvé",
        description: "Le rapport n'a pas encore été généré pour cette campagne.",
        variant: "destructive",
      });
      return;
    }

    // Rediriger vers la page Reports avec le rapport sélectionné
    navigate('/reports', { 
      state: { 
        selectedReportId: report.id,
        fromNotification: true 
      } 
    });
    
    toast({
      title: "Ouverture du rapport",
      description: "Le rapport s'ouvre. Utilisez le bouton PDF dans le rapport pour télécharger.",
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-[16px] border border-[hsl(var(--app-border-subtle))] bg-white text-[hsl(var(--app-text-muted))] shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-colors hover:bg-[hsl(var(--app-surface-muted))] hover:text-[hsl(var(--app-text-strong))]"
          aria-label="Voir les notifications"
        >
          <Bell className="h-4 w-4" strokeWidth={1.9} />
          {hasUnread && <span className="absolute right-2.5 top-2.5 h-2 w-2 rounded-full bg-[#FF515D] ring-2 ring-white" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="mt-2 w-[430px] overflow-hidden rounded-[24px] border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] p-0 shadow-[0_20px_48px_rgba(15,23,42,0.14)]"
        align="end"
        side="bottom"
      >
        <div className="flex max-h-[560px] flex-col overflow-hidden rounded-[24px] bg-[hsl(var(--app-surface))]">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--app-border-subtle))] px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-[hsl(var(--app-accent-soft))] text-[hsl(var(--app-accent-strong))]">
                <Bell className="h-5 w-5" strokeWidth={1.9} />
              </div>
              <div>
                <div className="text-[18px] font-semibold tracking-[-0.02em] text-[hsl(var(--app-text-strong))]">
                  Notifications {unreadCount > 0 ? `(${unreadCount})` : ""}
                </div>
                <div className="text-[12px] text-[hsl(var(--app-text-muted))]">
                  Activité récente et actions disponibles
                </div>
              </div>
            </div>
            {unreadCount > 0 && onMarkAllAsRead && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMarkAllAsRead();
                  toast({
                    title: "Notifications marquées",
                    description: `${unreadCount} notification${unreadCount > 1 ? 's' : ''} marquée${unreadCount > 1 ? 's' : ''} comme lue${unreadCount > 1 ? 's' : ''}`,
                  });
                }}
                className="text-[13px] font-medium text-[hsl(var(--app-accent-strong))] transition-colors hover:text-[hsl(var(--app-text-strong))]"
              >
                <span className="inline-flex items-center gap-1.5">
                  <CheckCheck className="h-4 w-4" />
                  Tout marquer comme lu
                </span>
              </button>
            )}
          </div>

          {/* Scrollable Notification Items */}
          <div className="flex-1 overflow-y-auto w-full">
            {notifications && notifications.length > 0 ? (
              notifications.map((notification, index) => (
                <div
                  key={notification.id || index}
                  className="w-full cursor-pointer border-b border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface))] px-5 py-4 transition-colors hover:bg-[hsl(var(--app-surface-muted))]"
                >
                  <div className="flex w-full items-start gap-3">
                    <NotificationIcon 
                      type={notification.type as NotificationType}
                      size="md"
                    />
                    <div className="flex-1">
                      <div className="flex w-full items-start justify-between gap-3">
                        <div className="flex-1 text-[15px] font-medium leading-6 text-[hsl(var(--app-text-strong))]">
                          {notification.message || 'Rapport de références généré pour Benjamin Leroy'}
                        </div>
                        <button
                          type="button"
                          className="rounded-lg p-1.5 text-[hsl(var(--app-text-muted))] transition-colors hover:bg-white hover:text-[hsl(var(--app-text-strong))]"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (notification.id) {
                              onMarkAsRead(notification.id);
                            }
                          }}
                          title="Marquer comme lu"
                        >
                          <MailOpen className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-1.5 flex w-full items-center justify-between gap-3">
                        <div className="text-[12px] text-[hsl(var(--app-text-muted))]">
                          {formatNotificationDate(notification.created_at)}
                        </div>
                        {notification.type === 'report_generated' && (
                          <button
                            type="button"
                            className="inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[13px] font-medium text-[hsl(var(--app-accent-strong))] transition-colors hover:bg-white hover:text-[hsl(var(--app-text-strong))]"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadReport(notification);
                            }}
                            title="Télécharger le rapport"
                          >
                            <Download className="h-4 w-4" />
                            Télécharger
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex w-full flex-col items-center justify-center gap-2 px-5 py-10">
                <div className="text-[14px] font-medium text-[hsl(var(--app-text))]">
                  Aucune notification
                </div>
                <div className="text-[12px] text-[hsl(var(--app-text-muted))]">
                  Vous êtes à jour pour le moment.
                </div>
              </div>
            )}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
